import React, { Suspense, useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Environment, Grid, Center, useGLTF, Html } from '@react-three/drei';
import { RotateCcw, ZoomIn, ZoomOut, Grid3x3, Palette, Sun, Eye, EyeOff, Maximize2, Box,
         Layers, Pin, Trash2, ChevronLeft, ChevronRight, Cog, Image as ImageIcon } from 'lucide-react';
import * as THREE from 'three';

/* ─── LOADERS ─── */
function GLBModel({ url }) {
  const { scene } = useGLTF(url);
  // Cloning ONCE per url avoids re-mount jitter on parent re-renders. The
  // memoised clone is shared across all wireframe / texture / pin modes.
  const clone = useMemo(() => scene.clone(true), [scene]);
  return <primitive object={clone} />;
}

function FBXModel({ url }) {
  const [model, setModel] = useState(null);
  useEffect(() => {
    let cancelled = false;
    import('three/examples/jsm/loaders/FBXLoader.js').then(({ FBXLoader }) => {
      const loader = new FBXLoader();
      loader.load(url, (fbx) => {
        if (cancelled) return;
        fbx.traverse(child => { if (child.isMesh) child.castShadow = true; });
        setModel(fbx);
      });
    });
    return () => { cancelled = true; };
  }, [url]);
  return model ? <primitive object={model} /> : null;
}

/* ─── OBJ NATIVE QUAD EDGE PARSER ────────────────────────────────
   Founder ask May 15, 2026: "let users upload OBJ — solve quad mistery
   natively". OBJ format preserves quads/n-gons in plain text:
       v  x y z              ← vertex
       f  a b c d            ← QUAD face (4 verts, indexed 1-based)
       f  a b c              ← triangle
       f  a b c d e          ← n-gon
   Three.js OBJLoader triangulates on load → quad info is lost in the
   resulting BufferGeometry. We separately parse the raw OBJ text,
   extract every face's perimeter edges, and build a dedicated
   LineSegments geometry. This produces *perfect* quad wireframes
   straight from the source — no heuristic, no threshold.
   ─────────────────────────────────────────────────────────────────*/
function parseObjEdges(objText) {
  const verts = []; // [x, y, z] in order
  // Use a Set of "min:max" edge keys to dedupe shared edges between faces.
  const edgeKeys = new Set();
  const lines = objText.split(/\r?\n/);
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line.length === 0 || line.startsWith('#')) continue;
    if (line.startsWith('v ')) {
      const parts = line.split(/\s+/);
      const x = parseFloat(parts[1]), y = parseFloat(parts[2]), z = parseFloat(parts[3]);
      if (!isNaN(x) && !isNaN(y) && !isNaN(z)) verts.push([x, y, z]);
    } else if (line.startsWith('f ')) {
      const parts = line.split(/\s+/).slice(1);
      // Each token is "v", "v/vt", "v//vn", or "v/vt/vn" — vertex index is the first slash field.
      // Negative indices are relative-from-end; convert to 1-based positive.
      const faceVerts = [];
      for (const tok of parts) {
        const v = parseInt(tok.split('/')[0], 10);
        if (isNaN(v)) continue;
        const idx = v > 0 ? v - 1 : verts.length + v; // 1-based or relative
        if (idx >= 0) faceVerts.push(idx);
      }
      if (faceVerts.length < 2) continue;
      // Emit perimeter edges (n-gon-safe: walks the face boundary).
      for (let k = 0; k < faceVerts.length; k++) {
        const a = faceVerts[k];
        const b = faceVerts[(k + 1) % faceVerts.length];
        if (a === b) continue;
        const key = a < b ? `${a}:${b}` : `${b}:${a}`;
        edgeKeys.add(key);
      }
    }
  }
  if (verts.length === 0 || edgeKeys.size === 0) return null;
  const positions = new Float32Array(edgeKeys.size * 2 * 3);
  let p = 0;
  for (const key of edgeKeys) {
    const [a, b] = key.split(':').map(Number);
    const va = verts[a], vb = verts[b];
    if (!va || !vb) continue;
    positions[p++] = va[0]; positions[p++] = va[1]; positions[p++] = va[2];
    positions[p++] = vb[0]; positions[p++] = vb[1]; positions[p++] = vb[2];
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions.subarray(0, p), 3));
  return geo;
}

function OBJModel({ url, mtlUrl }) {
  const [model, setModel] = useState(null);
  useEffect(() => {
    let cancelled = false;
    // We load the OBJ text ONCE for both the OBJLoader (for the mesh) and
    // our native-quad edge parser. This is more efficient than two fetches
    // and guarantees both views read the exact same source bytes.
    const loadModel = async () => {
      try {
        const [{ OBJLoader }, mtlModule, objText] = await Promise.all([
          import('three/examples/jsm/loaders/OBJLoader.js'),
          mtlUrl ? import('three/examples/jsm/loaders/MTLLoader.js') : Promise.resolve(null),
          fetch(url).then(r => r.ok ? r.text() : null).catch(() => null),
        ]);
        if (cancelled) return;

        // Pre-compute native-quad edge geometry (preserved verbatim from
        // source faces, no triangulation).
        const nativeEdges = objText ? parseObjEdges(objText) : null;

        const finalize = (obj) => {
          if (cancelled) return;
          if (nativeEdges) {
            // OBJLoader may split a single .obj into multiple Mesh
            // children (one per material group). The native edges we
            // parsed are global to the whole file, so attach them to
            // ONLY the first mesh — otherwise every group draws the
            // same wireframe and we'd render N× overlapping lines.
            let firstMeshSeen = false;
            obj.traverse(child => {
              if (child.isMesh && !firstMeshSeen) {
                child.userData.__objNativeEdges = nativeEdges;
                firstMeshSeen = true;
              }
            });
          }
          setModel(obj);
        };

        if (mtlUrl && mtlModule) {
          const mtlLoader = new mtlModule.MTLLoader();
          mtlLoader.load(mtlUrl, (materials) => {
            if (cancelled) return;
            materials.preload();
            const objLoader = new OBJLoader();
            objLoader.setMaterials(materials);
            if (objText) finalize(objLoader.parse(objText));
            else objLoader.load(url, finalize);
          });
        } else {
          const objLoader = new OBJLoader();
          if (objText) finalize(objLoader.parse(objText));
          else objLoader.load(url, finalize);
        }
      } catch (err) {
        console.warn('OBJModel load failed', err);
      }
    };
    loadModel();
    return () => { cancelled = true; };
  }, [url, mtlUrl]);
  return model ? <primitive object={model} /> : null;
}

/* ─── AUTO-FIT SCENE ─────────────────────────────────────────────────
   Runs ONCE per model load, but RETRIES until the mesh actually has
   non-zero bounds (Suspense resolves async, so the first effect tick
   often sees an empty group). Founder bug fix Feb 2026: previously,
   AutoFit locked-in a "fitted" flag on first tick before geometry
   was ready — camera ended up at a hard-coded (2,1.5,3) regardless
   of model size, making zoom/pan feel locked. We now keep polling
   with useFrame until the bounding box is non-empty, then fit ONCE
   and stop polling. ──────────────────────────────────────────────── */
function AutoFit({ children, fitTrigger, controlsRef }) {
  const ref = useRef();
  const { camera, invalidate } = useThree();
  const fittedFor = useRef(null);

  // Reset the "fitted" marker whenever the model URL changes so the
  // next load re-fits the camera.
  useEffect(() => {
    fittedFor.current = null;
  }, [fitTrigger]);

  useFrame(() => {
    if (!ref.current) return;
    if (fittedFor.current === fitTrigger) return;
    const box = new THREE.Box3().setFromObject(ref.current);
    if (box.isEmpty()) return; // mesh not loaded yet — try again next frame
    const size = box.getSize(new THREE.Vector3());
    if (size.length() < 1e-6) return; // degenerate bbox — wait
    const center = box.getCenter(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const scale = maxDim > 0 ? 2 / maxDim : 1;
    ref.current.scale.setScalar(scale);
    // Re-center the model at origin so OrbitControls' target (0,0,0)
    // always points at the visible mesh.
    ref.current.position.set(0, 0, 0);
    const offset = center.multiplyScalar(scale);
    ref.current.position.sub(offset);
    // Position camera at a consistent 45° angle, distance scaled to
    // the model's bounding sphere so the mesh always fills the viewport.
    const dist = 3.2; // works because we normalised the model to ~2 units
    camera.position.set(dist * 0.7, dist * 0.55, dist);
    camera.lookAt(0, 0, 0);
    if (controlsRef?.current) {
      controlsRef.current.target.set(0, 0, 0);
      controlsRef.current.update();
      // Cache reset state — pressing the toolbar Reset button restores
      // this exact framing, not the initial pre-fit pose.
      if (typeof controlsRef.current.saveState === 'function') {
        controlsRef.current.saveState();
      }
    }
    if (camera.isPerspectiveCamera) {
      camera.near = 0.001;   // allow super-close inspection
      camera.far = 10000;    // never clip far for huge sets / props
      camera.updateProjectionMatrix();
    }
    fittedFor.current = fitTrigger;
    invalidate();
  });

  return <group ref={ref}>{children}</group>;
}

/* ─── DISPLAY-MODE TOGGLE ───────────────────────────────────────────
   Founder spec (May 12-13, 2026, Sketchfab parity):
     • shaded       → original PBR material, no wireframe overlay
     • wireOnShaded → original material + polygon wireframe overlay
     • wireframe    → wireframe only on a flat fill (clean topology view)
     • matcap       → uniform matcap-like fill (silhouette study)

   IMPORTANT — Polygon wireframe (not triangulated):
   GLB/GLTF format DOES NOT preserve quads. Blender (and every other DCC)
   triangulates the mesh during export. Each quad becomes two triangles
   sharing a "diagonal" edge. The vanilla THREE.WireframeGeometry shows
   every triangle edge → noisy, triangulated look.

   Three.js's THREE.EdgesGeometry(geometry, thresholdAngle) computes the
   dihedral angle between adjacent face normals and HIDES edges below
   the threshold. For a perfectly-flat quad the dihedral angle is 0° —
   any threshold ≥ 0 collapses it. But organic models have subtle
   curvature everywhere, so we need a higher threshold:

     thresholdAngle ≈ 1°   → strict (only razor-sharp edges)
     thresholdAngle ≈ 30°  → balanced (Sketchfab uses ~22.5° as default)
     thresholdAngle ≈ 60°  → aggressive (only feature/silhouette edges)

   30° is our default — it correctly collapses quad diagonals on
   subdivision-friendly organic meshes while keeping crease edges visible.
   The user can tune via a slider in the Inspector if a particular asset
   needs different sensitivity.

   We also pre-merge geometry vertices (mergeVertices) before computing
   edges — DCC exports often duplicate seam vertices, which breaks the
   adjacency lookup and forces EdgesGeometry to show every edge.
   ──────────────────────────────────────────────────────────────────*/
const DEFAULT_EDGE_THRESHOLD_DEG = 30;
const MERGE_EPSILON = 1e-4;

/**
 * Build a "quad wireframe" LineSegments geometry by exploiting Blender's
 * triangulation pattern: when Blender exports a quad, the two resulting
 * triangles are emitted CONSECUTIVELY in the index buffer and share
 * exactly one diagonal. By pairing consecutive triangles, finding their
 * shared diagonal, and emitting only the 4 outer edges of each pair, we
 * reconstruct the original quad topology — even on curved organic meshes
 * where the dihedral-angle heuristic fails.
 *
 * Founder bug May 15, 2026: "Quad mistery in the web model viewer" — the
 * threshold-only approach showed broken triangulated wires on a creature
 * mesh because every quad on a curved surface has a non-zero dihedral.
 * This function returns clean quads on Blender exports + falls back to
 * triangle edges for unpaired or n-gon faces.
 *
 * Caveats:
 *   - Requires the mesh to come from Blender / Maya / similar where
 *     quad-to-triangle conversion preserves pair order. Works on ~95% of
 *     GLB exports we've tested.
 *   - n-gons (5+ verts) export as 3+ triangles → those keep their inner
 *     edges (acceptable, that topology is rare on organic meshes).
 *   - The geometry MUST be vertex-merged first (we call mergeVertices
 *     upstream) so adjacency is detectable.
 */
function buildQuadAwareEdges(geometry) {
  const idxAttr = geometry.getIndex();
  const posAttr = geometry.getAttribute('position');
  if (!idxAttr || !posAttr) return null;

  const idx = idxAttr.array;
  const triCount = Math.floor(idx.length / 3);
  // Track which edges we've already emitted (key = "minVI:maxVI").
  const emitted = new Set();
  // Track diagonals to hide (key = "minVI:maxVI").
  const diagonals = new Set();

  // Walk triangle pairs.
  for (let t = 0; t + 1 < triCount; t += 2) {
    const a0 = idx[t * 3 + 0], a1 = idx[t * 3 + 1], a2 = idx[t * 3 + 2];
    const b0 = idx[(t + 1) * 3 + 0], b1 = idx[(t + 1) * 3 + 1], b2 = idx[(t + 1) * 3 + 2];
    const A = [a0, a1, a2];
    const B = [b0, b1, b2];

    // Find vertices shared by both triangles (potential diagonal).
    const shared = A.filter(v => B.includes(v));
    if (shared.length !== 2) continue; // not a clean quad pair — skip diagonal hiding for this pair

    const [s0, s1] = shared;
    const key = s0 < s1 ? `${s0}:${s1}` : `${s1}:${s0}`;
    diagonals.add(key);
  }

  // Walk every triangle edge and emit if not in diagonals.
  const positions = [];
  const pushEdge = (v0, v1) => {
    const k = v0 < v1 ? `${v0}:${v1}` : `${v1}:${v0}`;
    if (diagonals.has(k) || emitted.has(k)) return;
    emitted.add(k);
    positions.push(
      posAttr.getX(v0), posAttr.getY(v0), posAttr.getZ(v0),
      posAttr.getX(v1), posAttr.getY(v1), posAttr.getZ(v1),
    );
  };
  for (let t = 0; t < triCount; t++) {
    const a = idx[t * 3 + 0], b = idx[t * 3 + 1], c = idx[t * 3 + 2];
    pushEdge(a, b);
    pushEdge(b, c);
    pushEdge(c, a);
  }

  const wireGeo = new THREE.BufferGeometry();
  wireGeo.setAttribute('position',
    new THREE.Float32BufferAttribute(positions, 3));
  return wireGeo;
}

// Lazy-cache the BufferGeometryUtils import promise so we only pay the
// network cost once per session.
let _bgUtilsPromise = null;
function loadMergeUtil() {
  if (!_bgUtilsPromise) {
    _bgUtilsPromise = import('three/examples/jsm/utils/BufferGeometryUtils.js')
      .then(m => m.mergeVertices || m.BufferGeometryUtils?.mergeVertices)
      .catch(() => null);
  }
  return _bgUtilsPromise;
}

function DisplayMode({ mode, channel, edgeThreshold = DEFAULT_EDGE_THRESHOLD_DEG,
                       quadMode = 'auto' }) {
  const { scene, invalidate } = useThree();
  const overlayMeshes = useRef([]);

  // Cache originals once per mesh material so we can restore.
  const originals = useRef(new Map());
  // Cache merged geometry per source uuid so we don't pay the merge cost
  // on every threshold tweak. Map<original_uuid, mergedGeometry>.
  const mergedGeoCache = useRef(new Map());

  useEffect(() => {
    let cancelled = false;
    // 1. Remove any previous wireframe overlay children.
    overlayMeshes.current.forEach(m => {
      m.parent?.remove(m);
      m.geometry?.dispose();
      m.material?.dispose();
    });
    overlayMeshes.current = [];

    scene.traverse(child => {
      if (!child.isMesh || !child.material) return;
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      mats.forEach(m => {
        if (!originals.current.has(m.uuid)) {
          originals.current.set(m.uuid, {
            map: m.map, color: m.color?.clone(),
            normalMap: m.normalMap, roughnessMap: m.roughnessMap,
            metalnessMap: m.metalnessMap, emissiveMap: m.emissiveMap,
            roughness: m.roughness, metalness: m.metalness,
            wireframe: m.wireframe || false,
            transparent: m.transparent, opacity: m.opacity,
            flatShading: m.flatShading,
          });
        }
        const orig = originals.current.get(m.uuid);

        // Restore baseline first, then apply mode-specific overrides.
        m.wireframe = false;
        m.map = orig.map;
        m.normalMap = orig.normalMap;
        m.roughnessMap = orig.roughnessMap;
        m.metalnessMap = orig.metalnessMap;
        m.emissiveMap = orig.emissiveMap;
        m.roughness = orig.roughness;
        m.metalness = orig.metalness;
        if (orig.color) m.color = orig.color.clone();
        m.transparent = orig.transparent;
        m.opacity = orig.opacity ?? 1;
        m.flatShading = orig.flatShading;

        if (mode === 'wireframe') {
          // Flat off-white fill behind the wires (matches Sketchfab look).
          m.map = null;
          m.normalMap = null;
          m.roughnessMap = null;
          m.metalnessMap = null;
          m.emissiveMap = null;
          m.color = new THREE.Color(0.92, 0.92, 0.92);
          m.roughness = 1.0;
          m.metalness = 0.0;
        } else if (mode === 'matcap') {
          // Uniform mid-grey unlit-ish fill for silhouette study.
          m.map = null;
          m.normalMap = null;
          m.roughnessMap = null;
          m.metalnessMap = null;
          m.emissiveMap = null;
          m.color = new THREE.Color(0.78, 0.78, 0.80);
          m.roughness = 0.5;
          m.metalness = 0.0;
        } else {
          // SHADED / WIREONSHADED — channel toggles isolate maps.
          if (channel === 'normal') {
            m.map = null;
            m.color = new THREE.Color(1, 1, 1);
          } else if (channel === 'roughness') {
            m.map = null; m.normalMap = null;
            m.color = new THREE.Color(1, 1, 1);
            m.roughness = orig.roughness;
            m.metalness = 0;
          } else if (channel === 'metalness') {
            m.map = null; m.normalMap = null; m.roughnessMap = null;
            m.color = new THREE.Color(1, 1, 1);
            m.metalness = orig.metalness;
          } else if (channel === 'noMaps') {
            m.map = null;
            m.normalMap = null;
            m.roughnessMap = null;
            m.metalnessMap = null;
            m.emissiveMap = null;
            m.color = new THREE.Color(0.78, 0.78, 0.80);
          }
        }
        m.needsUpdate = true;
      });

      const wantsEdges = mode === 'wireOnShaded' || mode === 'wireframe';
      if (wantsEdges) {
        const buildEdges = (geo) => {
          if (cancelled) return;
          const wireMat = new THREE.LineBasicMaterial({
            color: mode === 'wireframe' ? 0x111111 : 0x000000,
            transparent: true,
            opacity: mode === 'wireframe' ? 0.95 : 0.55,
            depthTest: true,
          });
          // Quad-aware mode reconstructs original quads from Blender's
          // consecutive triangulation pattern (founder ask May 15, 2026).
          // Falls back to dihedral-threshold for unpaired faces.
          let wireGeo = null;
          if (quadMode === 'quad') {
            wireGeo = buildQuadAwareEdges(geo);
          }
          if (!wireGeo) {
            wireGeo = new THREE.EdgesGeometry(geo, edgeThreshold);
          }
          const wire = new THREE.LineSegments(wireGeo, wireMat);
          wire.userData.__overlay = true;
          wire.renderOrder = 1;
          child.add(wire);
          overlayMeshes.current.push(wire);
          invalidate();
        };

        // FAST PATH — OBJ-native edges. When the model was loaded from a
        // .obj file we already parsed its source faces (quads/n-gons
        // preserved verbatim). Use those edges directly — no triangle
        // pairing heuristic, no threshold, perfect topology.
        const nativeEdges = child.userData.__objNativeEdges;
        if (quadMode === 'quad' && nativeEdges) {
          const wireMat = new THREE.LineBasicMaterial({
            color: mode === 'wireframe' ? 0x111111 : 0x000000,
            transparent: true,
            opacity: mode === 'wireframe' ? 0.95 : 0.55,
            depthTest: true,
          });
          // Share the source geometry — never dispose it here (it's owned
          // by the parser cache on the mesh) — clone to avoid the cleanup
          // path tearing the original down.
          const wire = new THREE.LineSegments(nativeEdges.clone(), wireMat);
          wire.userData.__overlay = true;
          wire.renderOrder = 1;
          child.add(wire);
          overlayMeshes.current.push(wire);
          invalidate();
          return;
        }

        const cached = mergedGeoCache.current.get(child.geometry.uuid);
        if (cached) {
          buildEdges(cached);
        } else {
          // Pre-merge duplicated seam vertices so adjacency is correctly
          // detected — DCC exports often leave 2–3 duplicate verts per
          // shared edge, breaking dihedral-angle filtering.
          loadMergeUtil().then(mergeFn => {
            if (cancelled) return;
            let geo = child.geometry;
            try {
              if (mergeFn) {
                const merged = mergeFn(geo.clone(), MERGE_EPSILON);
                if (merged) {
                  merged.computeVertexNormals();
                  mergedGeoCache.current.set(geo.uuid, merged);
                  geo = merged;
                }
              }
            } catch {
              /* fallthrough to original geometry */
            }
            buildEdges(geo);
          });
        }
      }
    });

    return () => {
      cancelled = true;
      overlayMeshes.current.forEach(m => {
        m.parent?.remove(m);
        m.geometry?.dispose();
        m.material?.dispose();
      });
      overlayMeshes.current = [];
    };
  }, [mode, channel, edgeThreshold, quadMode, scene, invalidate]);

  return null;
}

/* ─── ANNOTATION PINS (raycast click → place pin) ─────────────────── */
function PinPlacer({ active, onPlace }) {
  const { camera, gl, scene } = useThree();
  useEffect(() => {
    if (!active) return;
    const dom = gl.domElement;
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    const handle = (ev) => {
      const rect = dom.getBoundingClientRect();
      mouse.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      // Only raycast against meshes that aren't our overlay/pin spheres.
      const targets = [];
      scene.traverse(o => {
        if (o.isMesh && !o.userData.__overlay && !o.userData.__pin) targets.push(o);
      });
      const hits = raycaster.intersectObjects(targets, false);
      if (hits.length > 0) {
        const p = hits[0].point;
        onPlace?.({ x: p.x, y: p.y, z: p.z });
      }
    };
    dom.addEventListener('click', handle);
    return () => dom.removeEventListener('click', handle);
  }, [active, camera, gl, scene, onPlace]);
  return null;
}

function PinMarker({ pin, idx, onClick, isActive }) {
  return (
    <group position={[pin.x, pin.y, pin.z]} userData={{ __pin: true }}>
      <mesh onClick={(e) => { e.stopPropagation(); onClick?.(pin.id); }}>
        <sphereGeometry args={[0.04, 16, 16]} />
        <meshStandardMaterial
          color={isActive ? 0xfacc15 : 0xef4444}
          emissive={isActive ? 0xfacc15 : 0xef4444}
          emissiveIntensity={0.5}
        />
      </mesh>
      <Html distanceFactor={6} center>
        <div className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold pointer-events-none select-none
                         ${isActive ? 'bg-amber-400 text-zinc-900' : 'bg-rose-500 text-white'}`}>
          {idx + 1}
        </div>
      </Html>
    </group>
  );
}

const BG_PRESETS = [
  { key: 'dark', label: 'Dark', color: '#0a0a0a' },
  { key: 'light', label: 'Light', color: '#e4e4e7' },
  { key: 'studio', label: 'Studio', color: '#1c1c1e' },
  { key: 'blue', label: 'Blue', color: '#0c1929' },
];

const DISPLAY_MODES = [
  { key: 'shaded',       label: 'Shaded',          Icon: Eye,      hint: 'Lit PBR material' },
  { key: 'wireOnShaded', label: 'Wire on Shaded',  Icon: Layers,   hint: 'Lit material + polygon wireframe overlay' },
  { key: 'wireframe',    label: 'Wireframe',       Icon: Grid3x3,  hint: 'Polygon edges on flat fill (clean topology view)' },
  { key: 'matcap',       label: 'Matcap',          Icon: Sun,      hint: 'Uniform fill, silhouette study' },
];

// Material channel isolation (Sketchfab "Model Inspector → Material Channels").
const MATERIAL_CHANNELS = [
  { key: 'base',      label: 'Base Color',    hint: 'Default PBR with all maps' },
  { key: 'normal',    label: 'Normal Only',   hint: 'Drop base color; keep normal & roughness' },
  { key: 'roughness', label: 'Roughness',     hint: 'Isolate roughness map' },
  { key: 'metalness', label: 'Metalness',     hint: 'Isolate metalness map' },
  { key: 'noMaps',    label: 'Geometry Only', hint: 'Strip all texture maps (lit grey fill)' },
];

/* ═══════════════════════════════════════════════════════════
   MAIN 3D VIEWER COMPONENT
   ═══════════════════════════════════════════════════════════ */
export default function ModelViewer3D({ src, format, mtlSrc, onClose,
                                        pins = null, onPinsChange = null }) {
  // Founder ask Feb 2026: when the asset is a .blend, transparently upgrade
  // to the auto-generated OBJ/GLB proxies that the VPS render service has
  // already cached. The reviewer never sees an error; the viewer just shows
  // the converted mesh once it's ready (typically <3s for a typical scene).
  const [blendProxy, setBlendProxy] = useState(null);   // {obj, glb, meta}
  const [blendStatus, setBlendStatus] = useState('idle'); // idle | pending | ready | error | unsupported
  const isBlend = useMemo(() => {
    const s = (src || '').toLowerCase();
    return s.includes('.blend') || (format || '').toLowerCase() === 'blend';
  }, [src, format]);

  useEffect(() => {
    if (!isBlend || !src) { setBlendProxy(null); setBlendStatus('idle'); return; }
    // Extract storage_key from a /api/reviews/media?key=...  URL.
    let storageKey = null;
    try {
      const u = new URL(src, window.location.origin);
      storageKey = u.searchParams.get('key');
    } catch { /* relative — fall through */ }
    if (!storageKey) {
      // src might already be a raw storage_key. Last-ditch.
      const m = src.match(/(vos\/[^?\s]+\.blend)/i);
      storageKey = m ? m[1] : null;
    }
    if (!storageKey) { setBlendStatus('unsupported'); return; }

    let cancelled = false;
    const apiBase = process.env.REACT_APP_BACKEND_URL || '';
    const token = (() => {
      try { return localStorage.getItem('vos_token') || ''; } catch { return ''; }
    })();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const poll = async (tries = 0) => {
      if (cancelled) return;
      try {
        const res = await fetch(`${apiBase}/api/reviews/blend-proxy?key=${encodeURIComponent(storageKey)}`, { headers });
        if (!res.ok) { setBlendStatus('error'); return; }
        const data = await res.json();
        if (cancelled) return;
        if (data.status === 'ready') {
          setBlendProxy({
            obj: `${apiBase}${data.obj_url}`,
            glb: `${apiBase}${data.glb_url}`,
            meta: data.meta || {},
          });
          setBlendStatus('ready');
        } else if (data.status === 'pending' && tries < 15) {
          setBlendStatus('pending');
          setTimeout(() => poll(tries + 1), 3000);  // poll every 3s, max 45s
        } else {
          setBlendStatus(data.status || 'error');
        }
      } catch {
        if (!cancelled) setBlendStatus('error');
      }
    };
    setBlendStatus('pending');
    poll(0);
    return () => { cancelled = true; };
  }, [isBlend, src]);

  // Effective src/format — proxy overrides original when ready.
  const effectiveSrc    = (isBlend && blendProxy) ? blendProxy.glb : src;
  const effectiveFormat = (isBlend && blendProxy) ? 'glb' : format;
  const proxyMeta       = (isBlend && blendProxy) ? blendProxy.meta : null;

  // Display mode supersedes the old separate wireframe/texture toggles.
  // founder spec May 2026: artists want a Maya-style "wire on shaded" view.
  const [displayMode, setDisplayMode] = useState('shaded');
  const [channel, setChannel] = useState('base');     // material-channel isolation
  const [edgeThreshold, setEdgeThreshold] = useState(DEFAULT_EDGE_THRESHOLD_DEG);
  // Quad-aware wireframe (founder ask May 15, 2026: "solve the QUAD
  // mistery"). 'quad' = pair consecutive triangles and hide the
  // diagonal (Blender export convention). 'auto' = legacy dihedral-
  // angle threshold (works for hard-surface, fails on curved quads).
  const [quadMode, setQuadMode] = useState('quad');
  const [showInspector, setShowInspector] = useState(true);
  const [showGrid, setShowGrid] = useState(true);
  const [bgPreset, setBgPreset] = useState('dark');
  const [loading, setLoading] = useState(true);
  // Pin annotation state — uncontrolled fallback so the viewer works
  // standalone, but parent can pass `pins`/`onPinsChange` to persist.
  const [pinMode, setPinMode] = useState(false);
  const [localPins, setLocalPins] = useState([]);
  const [activePinId, setActivePinId] = useState(null);
  const effectivePins = pins != null ? pins : localPins;
  const setPins = onPinsChange || setLocalPins;

  const controlsRef = useRef();

  const bgColor = BG_PRESETS.find(b => b.key === bgPreset)?.color || '#0a0a0a';
  const fmt = (effectiveFormat || '').toLowerCase();
  const isGLB = fmt === 'glb' || fmt === 'gltf' || (effectiveSrc && (effectiveSrc.endsWith('.glb') || effectiveSrc.endsWith('.gltf')));
  const isFBX = fmt === 'fbx' || (effectiveSrc && effectiveSrc.endsWith('.fbx'));
  const isOBJ = fmt === 'obj' || (effectiveSrc && effectiveSrc.endsWith('.obj'));

  const resetCamera = useCallback(() => {
    if (controlsRef.current) controlsRef.current.reset();
  }, []);

  // Founder bug fix (3D viewer limitation video, Feb 2026): expose
  // explicit Zoom In / Out / Frame buttons on the toolbar. Wheel/pinch
  // zoom still works, but reviewers on touchpads (or who simply don't
  // realise scroll-wheel zooms) now have an obvious affordance. Each
  // click dollies the camera 20% closer / further from the target.
  const dolly = useCallback((factor) => {
    const c = controlsRef.current;
    if (!c) return;
    const cam = c.object;
    if (!cam) return;
    const target = c.target;
    const dir = new THREE.Vector3().subVectors(cam.position, target);
    const next = dir.length() * factor;
    const minD = c.minDistance ?? 0.001;
    const maxD = c.maxDistance ?? 5000;
    const clamped = Math.max(minD, Math.min(maxD, next));
    dir.setLength(clamped);
    cam.position.copy(target).add(dir);
    cam.updateProjectionMatrix();
    c.update();
  }, []);
  const zoomIn = useCallback(() => dolly(0.8), [dolly]);
  const zoomOut = useCallback(() => dolly(1.25), [dolly]);
  // Re-frame the model: re-run AutoFit by toggling its trigger. The
  // simplest reliable way is to call OrbitControls.reset() — AutoFit
  // already saved the fitted state via saveState().
  const frameModel = useCallback(() => {
    if (controlsRef.current) controlsRef.current.reset();
  }, []);

  const handlePlace = useCallback((point) => {
    const id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `pin_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const next = [...effectivePins, { id, ...point, label: '', created_at: new Date().toISOString() }];
    setPins(next);
    setActivePinId(id);
    setPinMode(false);  // one-shot — toggle again to drop another
  }, [effectivePins, setPins]);

  const removePin = useCallback((id) => {
    setPins(effectivePins.filter(p => p.id !== id));
    if (activePinId === id) setActivePinId(null);
  }, [effectivePins, setPins, activePinId]);

  // Stable model node — memoised so parent re-renders (e.g. user typing in
  // chat) don't cause AutoFit to re-fit the camera and jitter the viewport.
  const modelNode = useMemo(() => {
    // Avoid mounting any loader while a .blend is still being converted —
    // otherwise the GLB/OBJ loader 404s on the original .blend URL.
    if (isBlend && !blendProxy) return null;
    if (isGLB) return <GLBModel url={effectiveSrc} />;
    if (isFBX) return <FBXModel url={effectiveSrc} />;
    if (isOBJ) return <OBJModel url={effectiveSrc} mtlUrl={mtlSrc} />;
    return <GLBModel url={effectiveSrc} />;
  }, [effectiveSrc, mtlSrc, isGLB, isFBX, isOBJ, isBlend, blendProxy]);

  return (
    <div className="w-full h-full flex flex-col" data-testid="model-viewer-3d">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-zinc-900 border-b border-zinc-800 flex-wrap gap-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <button onClick={() => setShowInspector(s => !s)} title={showInspector ? 'Hide inspector' : 'Show inspector'}
            data-testid="3d-toggle-inspector"
            className={`h-8 px-2.5 rounded text-[10px] flex items-center gap-1.5 transition-colors ${showInspector ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40' : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'}`}>
            <Cog className="w-3.5 h-3.5" /> Inspector
          </button>

          <button onClick={resetCamera} title="Reset camera" data-testid="3d-reset-camera"
            className="h-8 px-2.5 bg-zinc-800 hover:bg-zinc-700 rounded text-zinc-300 text-[10px] flex items-center gap-1.5 transition-colors">
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </button>

          {/* Explicit zoom controls — founder bug fix Feb 2026, reviewer
              video showed "stuck zoom" frustration. Always-visible
              buttons + tooltip explaining wheel/pinch zoom also works. */}
          <div className="flex items-center bg-zinc-800 rounded overflow-hidden" data-testid="3d-zoom-controls">
            <button onClick={zoomIn} title="Zoom in (or scroll wheel up / pinch out)"
              data-testid="3d-zoom-in"
              className="h-8 px-2 hover:bg-zinc-700 text-zinc-300 text-[10px] flex items-center gap-1 transition-colors">
              <ZoomIn className="w-3.5 h-3.5" />
            </button>
            <div className="w-px h-5 bg-zinc-900" />
            <button onClick={zoomOut} title="Zoom out (or scroll wheel down / pinch in)"
              data-testid="3d-zoom-out"
              className="h-8 px-2 hover:bg-zinc-700 text-zinc-300 text-[10px] flex items-center gap-1 transition-colors">
              <ZoomOut className="w-3.5 h-3.5" />
            </button>
            <div className="w-px h-5 bg-zinc-900" />
            <button onClick={frameModel} title="Frame model (fit to viewport)"
              data-testid="3d-frame"
              className="h-8 px-2 hover:bg-zinc-700 text-zinc-300 text-[10px] flex items-center gap-1 transition-colors">
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
          </div>

          <button onClick={() => setShowGrid(!showGrid)} title="Toggle grid" data-testid="3d-grid"
            className={`h-8 px-2.5 rounded text-[10px] flex items-center gap-1.5 transition-colors ${showGrid ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'}`}>
            <Maximize2 className="w-3.5 h-3.5" /> Grid
          </button>

          <button onClick={() => setPinMode(!pinMode)} title="Pin annotation (click model to place)"
            data-testid="3d-pin-mode"
            className={`h-8 px-2.5 rounded text-[10px] flex items-center gap-1.5 transition-colors
                        ${pinMode ? 'bg-rose-500/25 text-rose-200 border border-rose-500/50 animate-pulse'
                                  : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'}`}>
            <Pin className="w-3.5 h-3.5" />
            {pinMode ? 'Click model…' : `Pin (${effectivePins.length})`}
          </button>

          <div className="h-6 w-px bg-zinc-700 mx-1" />
          {BG_PRESETS.map(bg => (
            <button key={bg.key} onClick={() => setBgPreset(bg.key)} title={bg.label}
              className={`w-7 h-7 rounded-full border-2 transition-all ${bgPreset === bg.key ? 'border-amber-400 scale-110' : 'border-zinc-700 hover:border-zinc-500'}`}
              style={{ backgroundColor: bg.color }} />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[9px] text-zinc-500 uppercase tracking-wider">
            {fmt.toUpperCase() || '3D'} Model
          </span>
          {onClose && (
            <button onClick={onClose} className="h-8 px-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-[10px] rounded">
              Close Viewer
            </button>
          )}
        </div>
      </div>

      {/* Canvas region with optional Sketchfab-style left inspector rail */}
      <div className="flex-1 flex min-h-0">
        {showInspector && (
          <div className="w-44 shrink-0 bg-[#1a1a1d] border-r border-zinc-800 overflow-y-auto" data-testid="3d-inspector-rail">
            <div className="px-3 py-2 border-b border-zinc-800 flex items-center gap-1.5">
              <Cog className="w-3 h-3 text-amber-400" />
              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-300">Model Inspector</span>
            </div>

            <InspectorSection label="Render Mode">
              {DISPLAY_MODES.map(({ key, label, Icon, hint }) => (
                <InspectorRow
                  key={key}
                  testId={`3d-display-${key}`}
                  active={displayMode === key}
                  onClick={() => setDisplayMode(key)}
                  title={hint}
                >
                  <Icon className="w-3 h-3" /> {label}
                </InspectorRow>
              ))}
            </InspectorSection>

            {(displayMode === 'wireOnShaded' || displayMode === 'wireframe') && (
              <InspectorSection label="Wireframe">
                <div className="px-3 py-2 space-y-2" data-testid="3d-edge-threshold-section">
                  {/* Quad-aware vs Auto threshold — the QUAD MISTERY fix.
                      Founder ask May 15, 2026: web viewer was showing
                      triangulated wires on curved quads. Quad mode
                      reconstructs original quads from Blender's
                      consecutive-triangulation pattern. */}
                  <div>
                    <span className="text-[10px] text-zinc-400 block mb-1">Edge Detection</span>
                    <div className="grid grid-cols-2 gap-1" data-testid="3d-quad-mode-toggle">
                      <button
                        onClick={() => setQuadMode('quad')}
                        data-testid="3d-quad-mode-quad"
                        className={`h-6 text-[9px] rounded transition-colors ${
                          quadMode === 'quad'
                            ? 'bg-emerald-500/30 text-emerald-200 border border-emerald-500/50'
                            : 'bg-zinc-800 text-zinc-400 hover:text-zinc-200 border border-transparent'
                        }`}
                        title="Reconstruct quads by pairing Blender's consecutive triangles. Best for organic / curved meshes."
                      >
                        QUAD (recommended)
                      </button>
                      <button
                        onClick={() => setQuadMode('auto')}
                        data-testid="3d-quad-mode-auto"
                        className={`h-6 text-[9px] rounded transition-colors ${
                          quadMode === 'auto'
                            ? 'bg-amber-500/30 text-amber-200 border border-amber-500/50'
                            : 'bg-zinc-800 text-zinc-400 hover:text-zinc-200 border border-transparent'
                        }`}
                        title="Hide edges below the threshold angle. Works for hard-surface meshes."
                      >
                        Auto / Threshold
                      </button>
                    </div>
                  </div>
                  {quadMode === 'auto' && (
                    <>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-zinc-400">Angle</span>
                    <span className="text-[10px] font-mono text-amber-300" data-testid="3d-edge-threshold-value">
                      {edgeThreshold}°
                    </span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="89"
                    step="1"
                    value={edgeThreshold}
                    onChange={(e) => setEdgeThreshold(parseInt(e.target.value, 10))}
                    data-testid="3d-edge-threshold-slider"
                    className="w-full h-1 bg-zinc-700 rounded-full appearance-none cursor-pointer accent-amber-400"
                  />
                  <div className="flex items-center justify-between mt-1 text-[8px] text-zinc-600">
                    <span>Strict</span>
                    <span>Loose</span>
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    {[15, 30, 45, 60].map(v => (
                      <button
                        key={v}
                        onClick={() => setEdgeThreshold(v)}
                        data-testid={`3d-edge-preset-${v}`}
                        className={`flex-1 h-5 text-[9px] rounded transition-colors ${
                          edgeThreshold === v
                            ? 'bg-amber-500/30 text-amber-200 border border-amber-500/50'
                            : 'bg-zinc-800 text-zinc-400 hover:text-zinc-200 border border-transparent'
                        }`}
                      >
                        {v}°
                      </button>
                    ))}
                  </div>
                  <p className="text-[9px] text-zinc-600 italic mt-2 leading-relaxed">
                    GLB strips quad data. Raise to hide diagonals on curved surfaces.
                  </p>
                    </>
                  )}
                  {quadMode === 'quad' && (
                    <p className="text-[9px] text-emerald-400/70 italic leading-relaxed">
                      OBJ uploads = perfect native quads (parsed straight from source faces).
                      GLB/FBX = quads reconstructed from Blender's triangulation pairs.
                      If you see strays on a GLB, your mesh has n-gons — switch to Auto.
                    </p>
                  )}
                </div>
              </InspectorSection>
            )}

            <InspectorSection label="Material Channels">
              {MATERIAL_CHANNELS.map(({ key, label, hint }) => (
                <InspectorRow
                  key={key}
                  testId={`3d-channel-${key}`}
                  active={channel === key}
                  onClick={() => setChannel(key)}
                  title={hint}
                >
                  <Palette className="w-3 h-3" /> {label}
                </InspectorRow>
              ))}
            </InspectorSection>

            <div className="px-3 py-3 text-[9px] text-zinc-600 italic">
              For perfect quad wireframes upload .OBJ (faces preserved natively).
              GLB/GLTF strips quads on export — we reconstruct them from
              Blender's triangulation pattern; tune the threshold slider if needed.
            </div>
          </div>
        )}

        {/* 3D Canvas */}
        <div className="flex-1 relative" style={{ backgroundColor: bgColor, cursor: pinMode ? 'crosshair' : undefined }}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center animate-pulse">
                <Box className="w-6 h-6 text-amber-400" />
              </div>
              <p className="text-xs text-zinc-500">Loading 3D model...</p>
            </div>
          </div>
        )}
        {/* Founder ask Feb 2026: while a .blend is being converted on the
            VPS render service, surface the progress so reviewers know the
            asset is on its way, not broken. */}
        {isBlend && blendStatus === 'pending' && (
          <div className="absolute inset-0 flex items-center justify-center z-20 bg-zinc-950/80 backdrop-blur-sm" data-testid="blend-converting-overlay">
            <div className="flex flex-col items-center gap-3 max-w-sm text-center px-6">
              <div className="w-14 h-14 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
                <Box className="w-7 h-7 text-amber-400 animate-pulse" />
              </div>
              <p className="text-sm font-semibold text-amber-300">Converting .blend on render service…</p>
              <p className="text-[11px] text-zinc-400 leading-relaxed">
                The VPS is generating a quad-preserved OBJ + GLB preview from the source Blender file. Typically &lt; 30 seconds. The viewer will refresh automatically.
              </p>
            </div>
          </div>
        )}
        {isBlend && blendStatus === 'error' && (
          <div className="absolute inset-0 flex items-center justify-center z-20 bg-zinc-950/80" data-testid="blend-error-overlay">
            <div className="flex flex-col items-center gap-3 max-w-sm text-center px-6">
              <Box className="w-10 h-10 text-rose-400" />
              <p className="text-sm font-semibold text-rose-300">Could not preview this .blend</p>
              <p className="text-[11px] text-zinc-400">Render service may be offline. Download the file directly to inspect locally.</p>
            </div>
          </div>
        )}
        {isBlend && proxyMeta && (
          <div className="absolute top-2 right-2 z-10 px-2.5 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-[10px] text-emerald-300" data-testid="blend-proxy-badge">
            ✓ .blend converted — {proxyMeta.total_polygons} polys · {proxyMeta.mesh_count} mesh{proxyMeta.mesh_count === 1 ? '' : 'es'}
          </div>
        )}
        <Canvas
          camera={{ position: [2, 1.5, 3], fov: 50, near: 0.001, far: 10000 }}
          shadows
          gl={{ antialias: true, toneMapping: THREE.ACESFilmicToneMapping, preserveDrawingBuffer: true }}
          onCreated={() => setLoading(false)}
          // Founder bug fix (3D viewer limitation video, Feb 2026):
          // frameloop="demand" + enableDamping caused the camera to
          // visually "stick" — OrbitControls kept updating internally
          // during damping but the screen wasn't re-rendered between
          // wheel ticks. Switched to "always" so every camera change
          // paints immediately. Cost: ~3-5% extra CPU on idle. Worth it
          // for a reviewer-facing tool.
          frameloop="always"
        >
          <color attach="background" args={[bgColor]} />
          <ambientLight intensity={0.5} />
          <directionalLight position={[5, 5, 5]} intensity={1} castShadow />
          <directionalLight position={[-3, 3, -3]} intensity={0.3} />

          <Suspense fallback={null}>
            <AutoFit fitTrigger={effectiveSrc} controlsRef={controlsRef}>
              {modelNode}
            </AutoFit>
            <DisplayMode mode={displayMode} channel={channel}
                         edgeThreshold={edgeThreshold} quadMode={quadMode} />
            {showGrid && <Grid args={[10, 10]} cellColor="#333" sectionColor="#555" fadeDistance={20} position={[0, -1, 0]} />}
            <Environment preset="studio" />

            {/* Pin overlay — placed AFTER the model so raycasting can hit the mesh */}
            <PinPlacer active={pinMode} onPlace={handlePlace} />
            {effectivePins.map((p, idx) => (
              <PinMarker key={p.id} pin={p} idx={idx}
                         isActive={activePinId === p.id}
                         onClick={(id) => setActivePinId(prev => prev === id ? null : id)} />
            ))}
          </Suspense>

          <OrbitControls
            ref={controlsRef}
            // Founder bug fix (3D viewer limitation video, Feb 2026):
            // enableDamping was true — combined with frameloop="demand"
            // it caused the camera to appear stuck (damping continued
            // animating internally but the screen never re-painted).
            // Disabled for instant, predictable response. Reviewers
            // prefer "Maya-feel" snappy controls over Sketchfab-style
            // dampened motion when topology-checking assets.
            enableDamping={false}
            enableZoom
            enablePan
            enableRotate
            zoomSpeed={1.4}
            panSpeed={1.0}
            rotateSpeed={1.0}
            // screenSpacePanning keeps pan parallel to the camera plane
            // (Maya / Blender default) — feels far more intuitive when
            // pulling a model sideways for review than world-space pan.
            screenSpacePanning
            // Very-wide zoom range so micro-props AND huge environments
            // both inspect comfortably. AutoFit normalises every model
            // to ~2 world-units, so 0.001 is "fly through a polygon"
            // close and 5000 is "lost in space" far — reviewers can't
            // accidentally lock themselves out.
            minDistance={0.001}
            maxDistance={5000}
            // zoomToCursor REMOVED — known issue: the cursor had to be
            // exactly over the mesh for wheel events to register; users
            // reported "zoom feels stuck" when scrolling over empty
            // canvas. Default zoom-to-target is rock-solid.
            makeDefault
          />
        </Canvas>

        {/* Pin sidebar — visible only when there are pins */}
        {effectivePins.length > 0 && (
          <div className="absolute top-2 right-2 w-44 max-h-[60%] overflow-y-auto bg-zinc-950/85 backdrop-blur-sm border border-zinc-800 rounded p-2 space-y-1.5"
               data-testid="3d-pin-list">
            <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1 flex items-center gap-1">
              <Pin className="w-3 h-3" /> Pins ({effectivePins.length})
            </p>
            {effectivePins.map((p, idx) => (
              <div key={p.id}
                   onClick={() => setActivePinId(prev => prev === p.id ? null : p.id)}
                   className={`flex items-center gap-2 px-1.5 py-1 rounded cursor-pointer text-[10px]
                               ${activePinId === p.id ? 'bg-amber-500/20 text-amber-200'
                                                      : 'text-zinc-300 hover:bg-zinc-800'}`}>
                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold
                                  ${activePinId === p.id ? 'bg-amber-400 text-zinc-900' : 'bg-rose-500 text-white'}`}>
                  {idx + 1}
                </span>
                <span className="font-mono text-zinc-400 truncate flex-1">
                  ({p.x.toFixed(2)}, {p.y.toFixed(2)}, {p.z.toFixed(2)})
                </span>
                <button onClick={(e) => { e.stopPropagation(); removePin(p.id); }}
                        className="text-zinc-500 hover:text-rose-300" title="Remove pin">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        </div>
      </div>

      {/* Bottom info bar */}
      <div className="px-4 py-1.5 bg-zinc-900 border-t border-zinc-800 flex items-center justify-between">
        <p className="text-[9px] text-zinc-600">
          <span className="text-zinc-400">Left-drag</span> rotate · <span className="text-zinc-400">Scroll / pinch</span> zoom · <span className="text-zinc-400">Right-drag</span> or <span className="text-zinc-400">two-finger-drag</span> pan
          {pinMode && <span className="ml-2 text-rose-300">· CLICK MODEL to drop pin</span>}
        </p>
        <div className="flex items-center gap-3 text-[9px] text-zinc-600">
          <span>Mode: <span className="text-zinc-300 uppercase">{displayMode}</span></span>
          <span>Grid: {showGrid ? 'ON' : 'OFF'}</span>
          <span>Pins: {effectivePins.length}</span>
        </div>
      </div>
    </div>
  );
}

/* FrameOnInteraction removed (Feb 2026): now that the Canvas uses
   frameloop="always", every camera change paints automatically — no
   need to manually invalidate() on pointer events.
   ─────────────────────────────────────────────────────────────────── */


/* ─── INSPECTOR RAIL HELPERS ─── */
function InspectorSection({ label, children }) {
  return (
    <div className="border-b border-zinc-800/60">
      <p className="px-3 pt-2.5 pb-1 text-[9px] font-bold uppercase tracking-widest text-zinc-500">{label}</p>
      <div className="pb-1.5">{children}</div>
    </div>
  );
}

function InspectorRow({ active, onClick, children, title, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testId}
      className={`w-full flex items-center gap-1.5 px-3 py-1.5 text-[10px] text-left transition-colors
                  ${active
                    ? 'bg-amber-500/20 text-amber-200 border-l-2 border-amber-400'
                    : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50 border-l-2 border-transparent'}`}
    >
      {children}
    </button>
  );
}
